<script setup>
/**
 * Renders an SVG-path shape with inner shadows using Canvas 2D.
 * Smooth morph animation between paths via numeric interpolation.
 * Auto-fits all paths to fill the canvas with optional padding.
 *
 * Props:
 *   paths           - [{ d, x?, y? }] SVG path d-strings with optional offset in CSS px
 *   activeIndex     - which path to display (morph triggered on change)
 *   fillColor       - CSS color for flat shape fill
 *   dropShadows     - [{ x, y, blur, spread, color }] layers behind the shape
 *   innerShadows    - [{ x, y, blur, spread, color }] layers inside the shape
 *   width           - CSS display width in px (responsive — changes on resize)
 *   height          - CSS display height in px (optional — auto-computed from aspect ratio)
 *   padding         - px of breathing room around the paths' bounding box
 *   bleed           - extra canvas space beyond width/height for shadow overflow
 *                      Number for uniform, or { top, right, bottom, left } for asymmetric
 *                      Does NOT affect shape sizing — shapes still fit width × height
 *   transitionDuration - ms for morph animation
 */

const props = defineProps({
    paths: { type: Array, required: true },
    activeIndex: { type: Number, default: 0 },
    fillColor: { type: String, default: '#D63D00' },
    dropShadows: { type: Array, default: () => [] },
    innerShadows: { type: Array, default: () => [] },
    width: { type: Number, required: true },
    height: { type: Number, default: null },
    padding: { type: Number, default: 0 },
    bleed: { type: [Number, Object], default: 0 },
    transitionDuration: { type: Number, default: 600 },
});

const canvasEl = ref(null);
const ready = ref(false);

let ctx = null;
let dpr = 1;
let tx = 0;     // translate X (in path coordinate space)
let ty = 0;     // translate Y
let scale = 1;  // uniform scale (path coords → CSS px)
let animRaf = null;
let reducedMotion = false;

function normalizeBleed(b) {
    if (typeof b === 'number') return { top: b, right: b, bottom: b, left: b };
    return { top: 0, right: 0, bottom: 0, left: 0, ...b };
}

// ---------------------------------------------------------------------------
// SVG Path Parsing
// ---------------------------------------------------------------------------

const CMD_RE = /([MmLlHhVvCcSsQqTtAaZz])\s*([\s\S]*?)(?=[MmLlHhVvCcSsQqTtAaZz]|$)/g;
const NUM_RE = /-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g;

function parsePath(d) {
    const commands = [];
    CMD_RE.lastIndex = 0;
    let match;
    while ((match = CMD_RE.exec(d)) !== null) {
        const type = match[1];
        const values = [];
        let numMatch;
        NUM_RE.lastIndex = 0;
        while ((numMatch = NUM_RE.exec(match[2])) !== null) {
            values.push(parseFloat(numMatch[0]));
        }
        commands.push({ type, values });
    }
    return commands;
}

function buildTemplate(commands) {
    return commands.map(c => ({ type: c.type, count: c.values.length }));
}

function extractValues(commands) {
    const vals = [];
    for (const cmd of commands) {
        for (const v of cmd.values) vals.push(v);
    }
    return new Float32Array(vals);
}

function rebuildDString(template, values) {
    let d = '';
    let idx = 0;
    for (const cmd of template) {
        d += cmd.type;
        for (let j = 0; j < cmd.count; j++) {
            if (j > 0) d += ' ';
            d += values[idx++];
        }
    }
    return d;
}

// Compute bounding box from all coordinate pairs across all path value arrays
function computeBounds(template, allValues) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    for (const values of allValues) {
        let idx = 0;
        for (const cmd of template) {
            const t = cmd.type.toUpperCase();
            const n = cmd.count;
            if (t !== 'Z') {
                for (let j = 0; j < n - 1; j += 2) {
                    const x = values[idx + j];
                    const y = values[idx + j + 1];
                    if (x < minX) minX = x;
                    if (x > maxX) maxX = x;
                    if (y < minY) minY = y;
                    if (y > maxY) maxY = y;
                }
            }
            idx += n;
        }
    }
    return { minX, minY, maxX, maxY };
}

function computeCentroid(template, values) {
    let sumX = 0, sumY = 0, count = 0;
    let idx = 0;
    for (const cmd of template) {
        const t = cmd.type.toUpperCase();
        const n = cmd.count;
        if (n >= 2 && t !== 'Z') {
            sumX += values[idx + n - 2];
            sumY += values[idx + n - 1];
            count++;
        }
        idx += n;
    }
    return count > 0 ? { x: sumX / count, y: sumY / count } : { x: 0, y: 0 };
}

// ---------------------------------------------------------------------------
// Reactive path data — parsed once, shared by bounds + preparePaths
// ---------------------------------------------------------------------------

const parsedPaths = computed(() => {
    if (!props.paths.length) return null;
    const parsed = props.paths.map(e => parsePath(e.d));
    return {
        template: buildTemplate(parsed[0]),
        values: parsed.map(extractValues),
        offsets: props.paths.map(e => ({ x: e.x || 0, y: e.y || 0 })),
    };
});

const shapeBounds = computed(() => {
    const pp = parsedPaths.value;
    if (!pp) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    return computeBounds(pp.template, pp.values);
});

// Auto-computed height: width drives scale, height follows aspect ratio
const effectiveHeight = computed(() => {
    if (props.height != null) return props.height;
    const bounds = shapeBounds.value;
    const bw = bounds.maxX - bounds.minX;
    const bh = bounds.maxY - bounds.minY;
    if (bw === 0) return 0;
    const pad = props.padding;
    const s = (props.width - pad * 2) / bw;
    return Math.ceil(bh * s + pad * 2);
});

const contentBounds = computed(() => ({
    width: props.width,
    height: effectiveHeight.value,
}));

defineExpose({ ready, effectiveHeight, contentBounds });

// Mutable render state (set by preparePaths, read by render/morphTo)
let cmdTemplate = null;
let pathValues = [];
let currentValues = null;
let centroid = { x: 0, y: 0 };
let pathOffsets = [];
let currentOffset = { x: 0, y: 0 };

const canvasSizeStyle = computed(() => {
    const bl = normalizeBleed(props.bleed);
    const h = effectiveHeight.value;
    const w = (props.width + bl.left + bl.right) + 'px';
    const hStr = (h + bl.top + bl.bottom) + 'px';
    const style = {
        width: w,
        height: hStr,
        minWidth: w,
        maxWidth: w,
        minHeight: hStr,
        maxHeight: hStr,
    };
    if (bl.left !== bl.right) style.marginLeft = -(bl.left - bl.right) / 2 + 'px';
    if (bl.top !== bl.bottom) style.marginTop = -(bl.top - bl.bottom) / 2 + 'px';
    return style;
});

// ---------------------------------------------------------------------------
// Prepare paths for rendering (reads from reactive computeds)
// ---------------------------------------------------------------------------

function preparePaths() {
    const pp = parsedPaths.value;
    if (!pp) return;

    cmdTemplate = pp.template;
    pathValues = pp.values;
    pathOffsets = pp.offsets;
    centroid = computeCentroid(cmdTemplate, pathValues[0]);

    const pad = props.padding;
    const bounds = shapeBounds.value;
    const bw = bounds.maxX - bounds.minX;
    const bh = bounds.maxY - bounds.minY;
    const h = effectiveHeight.value;
    scale = Math.min((props.width - pad * 2) / bw, (h - pad * 2) / bh);
    tx = -bounds.minX + (props.width / scale - bw) / 2;
    ty = -bounds.minY + (h / scale - bh) / 2;
}

// ---------------------------------------------------------------------------
// Interpolation
// ---------------------------------------------------------------------------

function lerpValues(a, b, t) {
    const out = new Float32Array(a.length);
    for (let i = 0; i < a.length; i++) {
        out[i] = a[i] + (b[i] - a[i]) * t;
    }
    return out;
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function render(values) {
    if (!ctx) return;
    const canvas = canvasEl.value;
    const h = effectiveHeight.value;

    const bl = normalizeBleed(props.bleed);

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(dpr * scale, dpr * scale);
    ctx.translate(
        bl.left / scale + tx + currentOffset.x / scale,
        bl.top / scale + ty + currentOffset.y / scale,
    );

    const dStr = rebuildDString(cmdTemplate, values);
    const path = new Path2D(dStr);

    // Drop shadow layers
    for (const shadow of props.dropShadows) {
        ctx.save();
        ctx.shadowOffsetX = shadow.x * scale * dpr;
        ctx.shadowOffsetY = shadow.y * scale * dpr;
        ctx.shadowBlur = shadow.blur * scale * dpr;
        ctx.shadowColor = shadow.color;

        if (shadow.spread) {
            const avgDim = (props.width + h) / 2 / scale;
            const s = 1 + (2 * shadow.spread) / avgDim;
            const mat = new DOMMatrix();
            mat.translateSelf(centroid.x, centroid.y);
            mat.scaleSelf(s, s);
            mat.translateSelf(-centroid.x, -centroid.y);
            const expanded = new Path2D();
            expanded.addPath(path, mat);
            ctx.fillStyle = shadow.color;
            ctx.fill(expanded);
        } else {
            ctx.fillStyle = shadow.color;
            ctx.fill(path);
        }
        ctx.restore();
    }

    // Fill the shape
    ctx.fillStyle = props.fillColor;
    ctx.fill(path);

    // Inner shadow layers
    for (const shadow of props.innerShadows) {
        ctx.save();
        ctx.clip(path);

        ctx.shadowOffsetX = shadow.x * scale * dpr;
        ctx.shadowOffsetY = shadow.y * scale * dpr;
        ctx.shadowBlur = shadow.blur * scale * dpr;
        ctx.shadowColor = shadow.color;

        const inverse = new Path2D();
        const margin = Math.max(shadow.blur * 2, 200);
        const ox = Math.abs(shadow.x);
        const oy = Math.abs(shadow.y);
        inverse.rect(
            -margin - ox - tx,
            -margin - oy - ty,
            props.width / scale + 2 * (margin + ox),
            h / scale + 2 * (margin + oy),
        );

        if (shadow.spread) {
            const avgDim = (props.width + h) / 2 / scale;
            const s = 1 - (2 * shadow.spread) / avgDim;
            const mat = new DOMMatrix();
            mat.translateSelf(centroid.x, centroid.y);
            mat.scaleSelf(s, s);
            mat.translateSelf(-centroid.x, -centroid.y);
            inverse.addPath(path, mat);
        } else {
            inverse.addPath(path);
        }

        ctx.fillStyle = shadow.color;
        ctx.fill(inverse, 'evenodd');
        ctx.restore();
    }

    ctx.restore();
}

// ---------------------------------------------------------------------------
// Animation
// ---------------------------------------------------------------------------

function easeInOut(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

function morphTo(targetValues, targetOffset) {
    if (!currentValues || reducedMotion) {
        currentValues = targetValues;
        currentOffset = targetOffset;
        render(currentValues);
        return;
    }

    const from = new Float32Array(currentValues);
    const fromOffset = { ...currentOffset };
    const to = targetValues;
    const toOffset = targetOffset;
    const start = performance.now();

    if (animRaf) cancelAnimationFrame(animRaf);

    const tick = () => {
        const t = Math.min((performance.now() - start) / props.transitionDuration, 1);
        const eased = easeInOut(t);
        currentValues = lerpValues(from, to, eased);
        currentOffset = {
            x: fromOffset.x + (toOffset.x - fromOffset.x) * eased,
            y: fromOffset.y + (toOffset.y - fromOffset.y) * eased,
        };
        render(currentValues);
        if (t < 1) {
            animRaf = requestAnimationFrame(tick);
        }
    };
    tick();
}

watch(() => props.activeIndex, (newIndex) => {
    if (!ready.value) return;
    if (newIndex < 0 || newIndex >= pathValues.length) return;
    morphTo(pathValues[newIndex], pathOffsets[newIndex]);
});

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

function resizeCanvas() {
    const canvas = canvasEl.value;
    if (!canvas) return;
    const bl = normalizeBleed(props.bleed);
    const h = effectiveHeight.value;
    canvas.width = Math.round((props.width + bl.left + bl.right) * dpr);
    canvas.height = Math.round((h + bl.top + bl.bottom) * dpr);
}

function refreshAll() {
    resizeCanvas();
    preparePaths();
    const idx = props.activeIndex;
    currentValues = pathValues[idx] || pathValues[0];
    currentOffset = pathOffsets[idx] || { x: 0, y: 0 };
    render(currentValues);
}

// Single watcher for all sizing/path inputs
watch(
    () => [props.width, props.height, props.bleed, props.paths, props.padding],
    () => { if (ready.value) refreshAll(); },
);

onMounted(async () => {
    await nextTick();
    const canvas = canvasEl.value;
    if (!canvas) return;

    dpr = Math.min(window.devicePixelRatio || 1, 2);
    ctx = canvas.getContext('2d');
    if (!ctx) return;

    reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    refreshAll();
    ready.value = true;
});

onUnmounted(() => {
    if (animRaf) cancelAnimationFrame(animRaf);
});
</script>

<template>
    <canvas ref="canvasEl"
        :class="ready ? 'opacity-100' : 'opacity-0'"
        class="transition-opacity duration-300"
        :style="canvasSizeStyle" />
</template>
